import type { ChangeEvent } from 'react';
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import Checkbox from '@/components/Checkbox';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { JobChangeProcessSchema } from '../../schemas/jobChangeProcessSchema';
import { jobChangeProcessSchema } from '../../schemas/jobChangeProcessSchema';
import { DISPLAY_ITEMS, RESULT_ITEMS, STEP_RANGE } from '../../constant';
import JobChangeStepRangeButton from './JobChangeStepRangeButton';
import useFetchActiveUsers from '../../../api/useFetchActiveUsers';
import CalendarRangeDropdownButton from './CalendarRangeDropdownButton';
import CalendarRangeDropdownMenu from './CalendarRangeDropdownMenu';
import JobChangeStepGauge from './JobChangeStepGauge';
import { useAtom } from 'jotai';
import {
  IN_PROGRESS_RESULTS,
  jobChangeProcessQueryAtom,
  jobChangeProcessSearchAtom,
} from '../../atoms/jobChangeProcess';
import { dateFormat } from '@/utils/dateFormat';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { useDebounce } from '@/hooks/useDebounce';
import Input from '@/components/Input';
import Select from '@/components/Select';
import MultiSelect from '@/components/MultiSelect';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdOutlineClose } from 'react-icons/md';
import { Button } from '@/components/Button';

{
  /* NOTE: 共通化の際にselectboxなどをあわせる */
}

// 「未設定」を表す担当者ID。BE 側で -1 を「担当者未設定」として扱う
const UNASSIGNED_USER_ID = -1;

type Props = {
  isOption: boolean;
  setIsOption: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function JobChangeProcessSearchOption({ isOption, setIsOption }: Props) {
  const [isCalendarDropdown, setIsCalendarDropdown] = useState(false);
  const [startStep, setStartStep] = useState<number | null>(null);
  const [endStep, setEndStep] = useState<number | null>(null);
  const { data: activeUsers } = useFetchActiveUsers();
  const [jobChangeProcessQuery, setJobChangeProcessQuery] = useAtom(jobChangeProcessQueryAtom);
  const [sharedSearch, setSharedSearch] = useAtom(jobChangeProcessSearchAtom);
  const calendarRef = useRef<HTMLDivElement>(null);
  const debounce = useDebounce(500);
  useOnClickOutside(calendarRef, () => setIsCalendarDropdown(false));

  const { register, getValues, setValue, control, watch } = useForm<JobChangeProcessSchema>({
    resolver: zodResolver(jobChangeProcessSchema),
    mode: 'all',
    defaultValues: {
      result: 'in_progress',
      stepRange: [],
      dateRange: {
        start: undefined,
        end: undefined,
      },
      scheduleStatuses: [],
      keyword: sharedSearch.keyword,
    },
  });

  // mount 時の書き込みは親の購読より先に走り取りこぼされるため、離脱時に既定へ戻す (#3507)
  useEffect(() => {
    return () => {
      setJobChangeProcessQuery((prev) => ({ ...prev, result: IN_PROGRESS_RESULTS }));
    };
  }, [setJobChangeProcessQuery]);

  const dateRange = watch('dateRange');

  const setDateRange = (newRange: { start: Date; end: Date }) => {
    setValue('dateRange', { start: newRange.start, end: newRange.end }, { shouldValidate: true });
    const customDateRange = {
      start: dateFormat(newRange.start),
      end: dateFormat(newRange.end),
    };
    setJobChangeProcessQuery({
      ...jobChangeProcessQuery,
      dateRange: customDateRange,
    });
  };

  const organizationUserOptions = useMemo(
    () => [
      { id: UNASSIGNED_USER_ID, name: '未設定' },
      ...(activeUsers?.records.map((u) => ({ id: u.id, name: u.name })) ?? []),
    ],
    [activeUsers]
  );

  const onCheckboxChange = (checked: boolean, value: string) => {
    const updatedStatuses = getValues('scheduleStatuses');

    if (checked) {
      setValue('scheduleStatuses', [...updatedStatuses, value]);
      setJobChangeProcessQuery({
        ...jobChangeProcessQuery,
        scheduleStatuses: [...updatedStatuses, value],
      });
    } else {
      const newStatuses = updatedStatuses.filter((status: string) => status !== value);
      setValue('scheduleStatuses', newStatuses);
      setJobChangeProcessQuery({
        ...jobChangeProcessQuery,
        scheduleStatuses: newStatuses,
      });
    }
  };

  const applyStepRange = (from: number, to: number) => {
    const rangeValues = STEP_RANGE.filter((r) => r.id >= from && r.id <= to).map((r) => r.value);
    setValue('stepRange', rangeValues);
    setJobChangeProcessQuery({
      ...jobChangeProcessQuery,
      stepRange: rangeValues,
    });
  };

  const handleStepClick = (stepId: number) => {
    if (startStep !== null && endStep === null && stepId >= startStep) {
      setEndStep(stepId);
      applyStepRange(startStep, stepId);
      return;
    }

    setStartStep(stepId);
    setEndStep(null);
    applyStepRange(stepId, stepId);
  };

  const onUpdateResult = (value: string) => {
    setValue('result', value);
    setJobChangeProcessQuery({
      ...jobChangeProcessQuery,
      result:
        value === 'all'
          ? ['processing', 'rejected', 'declined', 'offered', 'closed']
          : value === 'in_progress'
            ? IN_PROGRESS_RESULTS
            : [value ?? 'in_progress'],
    });
  };

  const onUpdateOrganization = (ids: number[]) => {
    setSharedSearch((prev) => ({ ...prev, organizationUserIds: ids }));
  };

  const onUpdateKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    debounce(() => {
      setSharedSearch((prev) => ({ ...prev, keyword: value }));
    });
  };

  const hasFormValues = useCallback(() => {
    const values = getValues();
    return (
      sharedSearch.keyword.trim() !== '' ||
      sharedSearch.organizationUserIds.length > 0 ||
      values.result !== 'in_progress' ||
      (values.scheduleStatuses && values.scheduleStatuses.length > 0) ||
      (values.stepRange && values.stepRange.length > 0) ||
      (values.dateRange &&
        (values.dateRange.start !== undefined || values.dateRange.end !== undefined))
    );
  }, [getValues, sharedSearch]);

  const clearSearchFilters = () => {
    // フォームをリセット
    setValue('keyword', '');
    setValue('result', 'in_progress');
    setValue('scheduleStatuses', []);
    setValue('stepRange', []);
    setValue('dateRange', { start: undefined, end: undefined });

    // ステップ範囲の状態をリセット
    setStartStep(null);
    setEndStep(null);

    // カレンダーの状態をリセット
    setIsCalendarDropdown(false);

    // クエリをリセット
    setSharedSearch({ keyword: '', organizationUserIds: [] });
    setJobChangeProcessQuery({
      ...jobChangeProcessQuery,
      result: IN_PROGRESS_RESULTS,
      scheduleStatuses: [],
      stepRange: [],
      dateRange: undefined,
    });
  };

  return (
    <form
      className="relative rounded bg-white p-md shadow-[0px_0px_2px_0px_rgba(0,_0,_0,_0.20)]"
      action={''}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="flex items-end gap-5">
        <div className="flex w-[422px] flex-col gap-2">
          <p className="text-sm font-bold">フリーワード</p>
          <Input
            search
            placeholder="フリーワードで絞り込む"
            {...register('keyword')}
            onChange={onUpdateKeyword}
            className="w-full"
          />
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">担当者</p>
          <div className="w-[196px]">
            <MultiSelect
              className="w-full"
              options={organizationUserOptions}
              value={sharedSearch.organizationUserIds}
              onChange={onUpdateOrganization}
              placeholder="担当者を選択"
              matchTriggerWidth
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">選考状況</p>
          <div className="w-[196px]">
            <Select items={RESULT_ITEMS} value={watch('result')} onValueChange={onUpdateResult} />
          </div>
        </div>

        {hasFormValues() && (
          <Button
            color="tertiary"
            size="md"
            className="ml-auto"
            type="button"
            onClick={clearSearchFilters}
          >
            <MdOutlineClose className="size-icon-xs text-primary" />
            <p className="text-sm font-bold text-primary">検索条件をクリア</p>
          </Button>
        )}
      </div>
      {isOption && (
        <div className="mt-5 flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-bold">表示項目</p>
            <div className="flex gap-4">
              {DISPLAY_ITEMS.map((i, key) => {
                return (
                  <Controller
                    key={key}
                    name="scheduleStatuses"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        value={i.value}
                        checked={field.value.includes(i.value)}
                        onChange={(checked) => onCheckboxChange(checked, i.value)}
                      >
                        {i.label}
                      </Checkbox>
                    )}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-bold">期間</p>
            <div className="relative w-[307px]" ref={calendarRef}>
              <CalendarRangeDropdownButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCalendarDropdown(!isCalendarDropdown);
                }}
                isOpen={isCalendarDropdown}
                dateRange={dateRange}
              />
              {isCalendarDropdown && (
                <div className="absolute top-xxl z-50">
                  <CalendarRangeDropdownMenu
                    dateRange={
                      dateRange
                        ? {
                            start: dateRange.start ?? null,
                            end: dateRange.end ?? null,
                          }
                        : undefined
                    }
                    setDateRange={setDateRange}
                    onClose={() => setIsCalendarDropdown(false)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm font-bold">進捗一覧</p>
            <div className="w-full overflow-x-auto">
              <div className="flex w-full min-w-fit gap-2">
                {STEP_RANGE.map((s) => {
                  const isStart = s.id === startStep;
                  const isEnd = s.id === endStep;
                  const isRange =
                    startStep !== null && endStep !== null && s.id > startStep && s.id < endStep;

                  return (
                    <div key={s.id} className="h-xl w-[118px] min-w-[118px] shrink-0">
                      <JobChangeStepRangeButton
                        title={s.label}
                        isStart={isStart}
                        isEnd={isEnd}
                        isRange={isRange}
                        value={s.id}
                        onClick={() => handleStepClick(s.id)}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="m-[0_auto] mt-3 w-full max-w-[1015px]">
                <JobChangeStepGauge startStep={startStep} endStep={endStep} />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-md flex justify-start">
        <Button color="tertiary" size="md" type="button" onClick={() => setIsOption(!isOption)}>
          {isOption ? (
            <MdKeyboardArrowUp className="size-icon-xs text-primary" />
          ) : (
            <MdKeyboardArrowDown className="size-icon-xs text-primary" />
          )}
          <span className="text-sm font-bold text-primary">
            {isOption ? '詳細条件を閉じる' : '詳細条件を開く'}
          </span>
        </Button>
      </div>
    </form>
  );
}
